import { useRef, useEffect } from "react";
import { LayoutGrid, Layers, Mountain, Palette, Share2 } from "lucide-react";
import { useTopographicMap } from "./TopographicMapContext";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { CheckboxField } from "../reusable/CheckboxField";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { PresetSelector } from "../reusable/PresetSelector";
import { SendSvgToToolMenu } from "../reusable/SendSvgToToolMenu";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";

export function TopographicMapControls() {
    const {
        canvasW,
        canvasH,
        sampleStep,
        seed,
        noiseScale,
        octaves,
        contourCount,
        indexEvery,
        indexStrokeWidthFactor,
        fillColor,
        fillColorHigh,
        strokeColor,
        indexStrokeColor,
        bgColor,
        strokeWidth,
        fillShapes,
        fillByAltitude,
        setCanvasW,
        setCanvasH,
        setSampleStep,
        setSeed,
        setNoiseScale,
        setOctaves,
        setContourCount,
        setIndexEvery,
        setIndexStrokeWidthFactor,
        setFillColor,
        setFillColorHigh,
        setStrokeColor,
        setIndexStrokeColor,
        setBgColor,
        setStrokeWidth,
        setFillShapes,
        setFillByAltitude,
        applyPreset,
        getPresetValues,
        resetParameters,
        randomizeParameters,
        canExport,
        buildExportSvgString,
        handleDownload,
    } = useTopographicMap();

    const fillColorTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const strokeColorTimeout = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );
    const indexStrokeColorTimeout = useRef<ReturnType<
        typeof setTimeout
    > | null>(null);
    const bgColorTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const fillColorHighTimeout = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );

    useEffect(() => {
        return () => {
            if (fillColorTimeout.current)
                clearTimeout(fillColorTimeout.current);
            if (strokeColorTimeout.current)
                clearTimeout(strokeColorTimeout.current);
            if (indexStrokeColorTimeout.current)
                clearTimeout(indexStrokeColorTimeout.current);
            if (bgColorTimeout.current) clearTimeout(bgColorTimeout.current);
            if (fillColorHighTimeout.current)
                clearTimeout(fillColorHighTimeout.current);
        };
    }, []);

    const handleFillColorPicker = (value: string) => {
        if (fillColorTimeout.current) clearTimeout(fillColorTimeout.current);
        fillColorTimeout.current = setTimeout(() => setFillColor(value), 150);
    };
    const handleStrokeColorPicker = (value: string) => {
        if (strokeColorTimeout.current)
            clearTimeout(strokeColorTimeout.current);
        strokeColorTimeout.current = setTimeout(
            () => setStrokeColor(value),
            150,
        );
    };
    const handleIndexStrokeColorPicker = (value: string) => {
        if (indexStrokeColorTimeout.current)
            clearTimeout(indexStrokeColorTimeout.current);
        indexStrokeColorTimeout.current = setTimeout(
            () => setIndexStrokeColor(value),
            150,
        );
    };
    const handleBgColorPicker = (value: string) => {
        if (bgColorTimeout.current) clearTimeout(bgColorTimeout.current);
        bgColorTimeout.current = setTimeout(() => setBgColor(value), 150);
    };
    const handleFillColorHighPicker = (value: string) => {
        if (fillColorHighTimeout.current)
            clearTimeout(fillColorHighTimeout.current);
        fillColorHighTimeout.current = setTimeout(
            () => setFillColorHigh(value),
            150,
        );
    };

    const handleInt =
        (setter: (v: number) => void, min = 1) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min) return;
            setter(Math.round(n));
        };

    const handleFloat =
        (setter: (v: number) => void, min = 0, max = Infinity) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min || n > max) return;
            setter(n);
        };

    const handleContourCount = (v: string | number) => {
        const n = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(n)) return;
        const r = Math.round(n);
        if (r < 8 || r > 40) return;
        setContourCount(r);
    };

    const handleIndexEvery = (v: string | number) => {
        const n = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(n)) return;
        const r = Math.round(n);
        if (r < 2 || r > 20) return;
        setIndexEvery(r);
    };

    return (
        <div className="panel-controls topographic-map-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Topographic Map</h2>
                    <ToolDescriptionPopover dialogLabel="Topographic Map tool description">
                        <p>
                            Iso-contours of a fractional-Brownian terrain field,
                            evenly spaced between min and max elevation. Index
                            contours (every Nth line) use a thicker stroke and a
                            second color, like a classic topo map.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={randomizeParameters}
                    >
                        Randomize parameters
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        disabled={!canExport}
                        onClick={handleDownload}
                    >
                        Download SVG
                    </button>
                </div>
            </div>

            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="topographic-map"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />

                <CollapsibleToolGroup title="Artboard" icon={LayoutGrid}>
                    <div className="field-row">
                        <label className="field-label">
                            Width (px)
                            <DebouncedInput
                                type="number"
                                min={100}
                                max={4000}
                                step={50}
                                value={canvasW}
                                onChange={handleInt(setCanvasW, 100)}
                                debounceMs={500}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Height (px)
                            <DebouncedInput
                                type="number"
                                min={100}
                                max={4000}
                                step={50}
                                value={canvasH}
                                onChange={handleInt(setCanvasH, 100)}
                                debounceMs={500}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Sample step (px)
                            <DebouncedInput
                                type="number"
                                min={2}
                                max={24}
                                step={1}
                                value={sampleStep}
                                onChange={handleInt(setSampleStep, 2)}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Terrain" icon={Mountain}>
                    <div className="field-row">
                        <label className="field-label">
                            Seed
                            <DebouncedInput
                                type="text"
                                value={seed}
                                onChange={(v) => setSeed(String(v))}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Noise scale
                            <DebouncedInput
                                type="number"
                                min={0.0005}
                                max={0.02}
                                step={0.0005}
                                value={noiseScale}
                                onChange={handleFloat(
                                    setNoiseScale,
                                    0.0005,
                                    0.02,
                                )}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Octaves
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={8}
                                step={1}
                                value={octaves}
                                onChange={handleInt(setOctaves, 1)}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Contours" icon={Layers}>
                    <div className="field-row">
                        <label className="field-label">
                            Contour count
                            <DebouncedInput
                                type="number"
                                min={8}
                                max={40}
                                step={1}
                                value={contourCount}
                                onChange={handleContourCount}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Index every Nth line
                            <DebouncedInput
                                type="number"
                                min={2}
                                max={20}
                                step={1}
                                value={indexEvery}
                                onChange={handleIndexEvery}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Index stroke width x
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={4}
                                step={0.05}
                                value={indexStrokeWidthFactor}
                                onChange={handleFloat(
                                    setIndexStrokeWidthFactor,
                                    1,
                                    4,
                                )}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Appearance" icon={Palette}>
                    <div className="field-row">
                        <CheckboxField
                            inline
                            checked={fillShapes}
                            onChange={setFillShapes}
                            label="Fill between contours (gradient opacity)"
                        />
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            inline
                            checked={fillByAltitude}
                            onChange={setFillByAltitude}
                            disabled={!fillShapes}
                            label="Color fill by elevation (low -> high)"
                        />
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            {fillByAltitude
                                ? "Fill color (low elevation)"
                                : "Fill color"}
                            <div className="control-color-row">
                                <input
                                    type="color"
                                    value={fillColor}
                                    onChange={(e) =>
                                        handleFillColorPicker(e.target.value)
                                    }
                                    style={{
                                        width: "50px",
                                        height: "30px",
                                        minWidth: "30px",
                                    }}
                                />
                                <DebouncedInput
                                    type="text"
                                    value={fillColor}
                                    onChange={(v) => setFillColor(String(v))}
                                    style={{
                                        flex: 1,
                                        padding: "4px",
                                        fontFamily: "monospace",
                                    }}
                                />
                            </div>
                        </label>
                    </div>
                    {fillShapes && fillByAltitude ? (
                        <div className="field-row">
                            <label className="field-label">
                                Fill color (high elevation)
                                <div className="control-color-row">
                                    <input
                                        type="color"
                                        value={fillColorHigh}
                                        onChange={(e) =>
                                            handleFillColorHighPicker(
                                                e.target.value,
                                            )
                                        }
                                        style={{
                                            width: "50px",
                                            height: "30px",
                                            minWidth: "30px",
                                        }}
                                    />
                                    <DebouncedInput
                                        type="text"
                                        value={fillColorHigh}
                                        onChange={(v) =>
                                            setFillColorHigh(String(v))
                                        }
                                        style={{
                                            flex: 1,
                                            padding: "4px",
                                            fontFamily: "monospace",
                                        }}
                                    />
                                </div>
                            </label>
                        </div>
                    ) : null}
                    <div className="field-row">
                        <label className="field-label">
                            Contour color
                            <div className="control-color-row">
                                <input
                                    type="color"
                                    value={strokeColor}
                                    onChange={(e) =>
                                        handleStrokeColorPicker(e.target.value)
                                    }
                                    style={{
                                        width: "50px",
                                        height: "30px",
                                        minWidth: "30px",
                                    }}
                                />
                                <DebouncedInput
                                    type="text"
                                    value={strokeColor}
                                    onChange={(v) => setStrokeColor(String(v))}
                                    style={{
                                        flex: 1,
                                        padding: "4px",
                                        fontFamily: "monospace",
                                    }}
                                />
                            </div>
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Index contour color
                            <div className="control-color-row">
                                <input
                                    type="color"
                                    value={indexStrokeColor}
                                    onChange={(e) =>
                                        handleIndexStrokeColorPicker(
                                            e.target.value,
                                        )
                                    }
                                    style={{
                                        width: "50px",
                                        height: "30px",
                                        minWidth: "30px",
                                    }}
                                />
                                <DebouncedInput
                                    type="text"
                                    value={indexStrokeColor}
                                    onChange={(v) =>
                                        setIndexStrokeColor(String(v))
                                    }
                                    style={{
                                        flex: 1,
                                        padding: "4px",
                                        fontFamily: "monospace",
                                    }}
                                />
                            </div>
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Background
                            <div className="control-color-row">
                                <input
                                    type="color"
                                    value={bgColor}
                                    onChange={(e) =>
                                        handleBgColorPicker(e.target.value)
                                    }
                                    style={{
                                        width: "50px",
                                        height: "30px",
                                        minWidth: "30px",
                                    }}
                                />
                                <DebouncedInput
                                    type="text"
                                    value={bgColor}
                                    onChange={(v) => setBgColor(String(v))}
                                    style={{
                                        flex: 1,
                                        padding: "4px",
                                        fontFamily: "monospace",
                                    }}
                                />
                            </div>
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Base stroke width
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={8}
                                step={0.1}
                                value={strokeWidth}
                                onChange={handleFloat(setStrokeWidth, 0, 8)}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>
                <CollapsibleToolGroup title="Send to tool" icon={Share2}>
                    <SendSvgToToolMenu
                        disabled={!canExport}
                        getSvg={() => ({
                            name: "topographic-map-export.svg",
                            content: buildExportSvgString(),
                        })}
                        allowedSlots={["path", "object", "primary"]}
                        excludeTargetPanels={["topographic-map"]}
                    />
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}
